import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

// ── helpers ──────────────────────────────────────────────────────────────────
const DAYS_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYS_AR = ["أحد", "إثن", "ثلا", "أرب", "خمي", "جمع", "سبت"];

const MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTHS_AR = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];
//lpl

const pad = (n) => String(n).padStart(2, "0");
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay();

const formatDisplayDate = (dateStr, isRTL) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  const months = isRTL ? MONTHS_AR : MONTHS_EN;
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

/**
 * DatePicker — A reusable, theme-aware calendar date picker.
 *
 * The calendar dropdown is rendered via a React Portal (document.body) so it
 * is never clipped by parent overflow, sidebars, or stacking contexts.
 *
 * Props:
 *   theme          – "dark" | "light"
 *   isRTL          – boolean (enables Arabic day/month names and RTL layout)
 *   label          – optional label text above the input
 *   value          – controlled date string in "YYYY-MM-DD" format
 *   onChange        – (dateString: string) => void
 *   placeholder    – optional placeholder text
 *   dropDirection  – "down" | "up" (default "down") — direction the calendar opens
 *   maxDate        – optional "YYYY-MM-DD" upper bound; later days are shown
 *                    greyed out and cannot be picked, and the calendar will
 *                    not page past that month. Used by Attendance to stop
 *                    anyone selecting a day after today (the backend rejects
 *                    future-dated attendance requests with 400).
 *   minDate        – optional "YYYY-MM-DD" lower bound (same behaviour)
 *   style          – optional style overrides for the outer container
 */
const DatePicker = ({
  theme = "light",
  isRTL = false,
  label,
  value,
  onChange,
  placeholder,
  dropDirection = "down",
  maxDate,
  minDate,
  style: outerStyle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    const d = value ? new Date(value + "T00:00:00") : new Date();
    return d.getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const d = value ? new Date(value + "T00:00:00") : new Date();
    return d.getMonth();
  });
  const [animation, setAnimation] = useState("");
  // "days" (default grid) | "months" | "years" — clicking the month or year
  // in the header jumps straight to a picker instead of paging month by month.
  const [pickerView, setPickerView] = useState("days");
  const [yearPageStart, setYearPageStart] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef(null);
  const calendarRef = useRef(null);

  // ── Position the portal dropdown relative to the trigger button ──────────
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const calendarHeight = 320; // approximate height of the smaller calendar
    const gap = 8;
    const dropWidth = Math.max(rect.width, 260);

    if (dropDirection === "up") {
      setDropdownPos({
        top: rect.top + window.scrollY - calendarHeight - gap,
        left: rect.left + window.scrollX,
        width: dropWidth,
      });
    } else {
      setDropdownPos({
        top: rect.bottom + window.scrollY + gap,
        left: rect.left + window.scrollX,
        width: dropWidth,
      });
    }
  }, [dropDirection]);

  // Position initially when opened
  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  // Close on outside click, scroll, or resize
  useEffect(() => {
    if (!isOpen) return;

    const onScroll = (e) => {
      if (calendarRef.current && calendarRef.current.contains(e.target)) return;
      setIsOpen(false);
    };

    const onResize = () => setIsOpen(false);

    const onOutsideClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        calendarRef.current && !calendarRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    document.addEventListener("mousedown", onOutsideClick);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousedown", onOutsideClick);
    };
  }, [isOpen]);

  // Sync view to selected date when calendar opens
  useEffect(() => {
    if (isOpen) setPickerView("days");
    if (isOpen && value) {
      const d = new Date(value + "T00:00:00");
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevMonth = useCallback(() => {
    setAnimation("slide-right");
    setTimeout(() => {
      setViewMonth((m) => {
        if (m === 0) { setViewYear((y) => y - 1); return 11; }
        return m - 1;
      });
      setAnimation("");
    }, 150);
  }, []);

  // ── Bounds helpers ────────────────────────────────────────────────────────
  const isOutOfRange = useCallback(
    (dateStr) => {
      if (!dateStr) return false;
      if (maxDate && dateStr > maxDate) return true;
      if (minDate && dateStr < minDate) return true;
      return false;
    },
    [maxDate, minDate]
  );

  // The whole displayed month sits past maxDate → block the ">" arrow.
  const canGoNextMonth = useMemo(() => {
    if (!maxDate) return true;
    const nextY = viewMonth === 11 ? viewYear + 1 : viewYear;
    const nextM = viewMonth === 11 ? 0 : viewMonth + 1;
    return `${nextY}-${pad(nextM + 1)}-01` <= maxDate;
  }, [maxDate, viewYear, viewMonth]);

  const canGoPrevMonth = useMemo(() => {
    if (!minDate) return true;
    const prevY = viewMonth === 0 ? viewYear - 1 : viewYear;
    const prevM = viewMonth === 0 ? 11 : viewMonth - 1;
    const lastDay = getDaysInMonth(prevY, prevM);
    return `${prevY}-${pad(prevM + 1)}-${pad(lastDay)}` >= minDate;
  }, [minDate, viewYear, viewMonth]);

  const nextMonth = useCallback(() => {
    setAnimation("slide-left");
    setTimeout(() => {
      setViewMonth((m) => {
        if (m === 11) { setViewYear((y) => y + 1); return 0; }
        return m + 1;
      });
      setAnimation("");
    }, 150);
  }, []);

  // ── Month / year pickers ──────────────────────────────────────────────────
  const YEARS_PER_PAGE = 12;
  const minYear = minDate ? Number(minDate.slice(0, 4)) : null;
  const maxYear = maxDate ? Number(maxDate.slice(0, 4)) : null;

  const isMonthOutOfRange = useCallback(
    (year, month) => {
      if (maxDate && `${year}-${pad(month + 1)}-01` > maxDate) return true;
      if (minDate && `${year}-${pad(month + 1)}-${pad(getDaysInMonth(year, month))}` < minDate) return true;
      return false;
    },
    [maxDate, minDate]
  );

  const isYearOutOfRange = (year) =>
    (minYear !== null && year < minYear) || (maxYear !== null && year > maxYear);

  const openYearPicker = () => {
    setYearPageStart(viewYear - (viewYear % YEARS_PER_PAGE));
    setPickerView("years");
  };

  // Picking a year keeps the current month when it's allowed, otherwise snaps
  // to the nearest month inside min/max, then returns to the day grid.
  const selectYear = (year) => {
    setViewYear(year);
    setViewMonth((m) => {
      if (!isMonthOutOfRange(year, m)) return m;
      if (maxDate && `${year}-${pad(m + 1)}-01` > maxDate) return Number(maxDate.slice(5, 7)) - 1;
      return Number(minDate.slice(5, 7)) - 1;
    });
    setPickerView("days");
  };

  const selectMonth = (month) => {
    if (isMonthOutOfRange(viewYear, month)) return;
    setViewMonth(month);
    setPickerView("days");
  };

  // Header arrows page by month / year / year-page depending on the view.
  const canGoPrev =
    pickerView === "days" ? canGoPrevMonth
    : pickerView === "months" ? minYear === null || viewYear - 1 >= minYear
    : minYear === null || yearPageStart - 1 >= minYear;
  const canGoNext =
    pickerView === "days" ? canGoNextMonth
    : pickerView === "months" ? maxYear === null || viewYear + 1 <= maxYear
    : maxYear === null || yearPageStart + YEARS_PER_PAGE <= maxYear;

  const handlePrev = () => {
    if (pickerView === "days") prevMonth();
    else if (pickerView === "months") setViewYear((y) => y - 1);
    else setYearPageStart((y) => y - YEARS_PER_PAGE);
  };
  const handleNext = () => {
    if (pickerView === "days") nextMonth();
    else if (pickerView === "months") setViewYear((y) => y + 1);
    else setYearPageStart((y) => y + YEARS_PER_PAGE);
  };

  const selectDay = useCallback(
    (day) => {
      const dateStr = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`;
      if (isOutOfRange(dateStr)) return;
      onChange?.(dateStr);
      setIsOpen(false);
    },
    [viewYear, viewMonth, onChange, isOutOfRange]
  );

  const goToToday = useCallback(() => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    if (isOutOfRange(todayStr)) {
      setIsOpen(false);
      return;
    }
    onChange?.(todayStr);
    setIsOpen(false);
  }, [onChange, isOutOfRange]);

  // ── calendar grid ──────────────────────────────────────────────────────────
  const calendarGrid = useMemo(() => {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
    const prevMonthDays = getDaysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth - 1);
    const cells = [];
    for (let i = firstDay - 1; i >= 0; i--) cells.push({ day: prevMonthDays - i, type: "prev" });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, type: "current" });
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) cells.push({ day: d, type: "next" });
    return cells;
  }, [viewYear, viewMonth]);

  const isDark = theme === "dark";

  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }, []);

  const isTodayOutOfRange = isOutOfRange(todayStr);

  const outOfRangeTitle = isRTL
    ? "هذا التاريخ خارج النطاق المسموح به"
    : "This date is outside the allowed range";

  const c = {
    bg: isDark ? "rgba(15, 23, 42, 0.5)" : "rgba(248, 250, 252, 0.8)",
    bgFocus: isDark ? "rgba(15, 23, 42, 0.7)" : "rgba(255, 255, 255, 0.95)",
    border: isDark ? "rgba(148, 163, 184, 0.3)" : "rgba(148, 163, 184, 0.3)",
    borderFocus: isDark ? "rgba(59, 130, 246, 0.5)" : "rgba(59, 130, 246, 0.6)",
    text: isDark ? "#f1f5f9" : "#1e293b",
    textMuted: isDark ? "#94a3b8" : "#64748b",
    textDim: isDark ? "#475569" : "#cbd5e1",
    accent: "#3b82f6",
    accentHover: "#2563eb",
    calBg: isDark ? "#0f172a" : "#ffffff",
    calBorder: isDark ? "rgba(148, 163, 184, 0.2)" : "rgba(0, 0, 0, 0.08)",
    dayHover: isDark ? "rgba(59, 130, 246, 0.15)" : "rgba(59, 130, 246, 0.08)",
    todayBg: isDark ? "rgba(59, 130, 246, 0.12)" : "rgba(59, 130, 246, 0.08)",
    iconColor: isDark ? "#ffffff" : "#000000",
  };

  const headerLinkStyle = {
    border: "none", background: "transparent", color: c.text,
    fontSize: "14px", fontWeight: "700", letterSpacing: "0.3px",
    padding: "4px 8px", borderRadius: "6px", cursor: "pointer",
    transition: "background 0.15s ease", fontFamily: "inherit",
  };

  // ── Calendar Portal ────────────────────────────────────────────────────────
  const calendarPortal = isOpen
    ? ReactDOM.createPortal(
        <div
          ref={calendarRef}
          data-datepicker-portal="true"
          style={{
            position: "absolute",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 99999,
            background: c.calBg,
            border: `1px solid ${c.calBorder}`,
            borderRadius: "16px",
            boxShadow: isDark
              ? "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)"
              : "0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)",
            overflow: "hidden",
            animation: `dpFadeIn 0.2s ease-out`,
          }}
        >
          {/* ── Month / Year header ──────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 12px 8px",
              borderBottom: `1px solid ${c.calBorder}`,
            }}
          >
            <button
              type="button"
              onClick={handlePrev}
              disabled={!canGoPrev}
              style={{
                width: "28px", height: "28px", borderRadius: "6px", border: "none",
                background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                color: c.text, cursor: canGoPrev ? "pointer" : "not-allowed",
                opacity: canGoPrev ? 1 : 0.4, display: "flex",
                alignItems: "center", justifyContent: "center", transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { if (canGoPrev) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: "2px", fontSize: "14px", fontWeight: "700", color: c.text, letterSpacing: "0.3px", userSelect: "none" }}>
              {pickerView === "years" ? (
                <span style={{ padding: "4px 8px", direction: "ltr" }}>
                  {yearPageStart} – {yearPageStart + YEARS_PER_PAGE - 1}
                </span>
              ) : (
                <>
                  {pickerView === "days" && (
                    <button
                      type="button"
                      onClick={() => setPickerView("months")}
                      title={isRTL ? "اختر الشهر" : "Choose month"}
                      style={headerLinkStyle}
                      onMouseEnter={(e) => { e.currentTarget.style.background = c.dayHover; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                      {(isRTL ? MONTHS_AR : MONTHS_EN)[viewMonth]}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={openYearPicker}
                    title={isRTL ? "اختر السنة" : "Choose year"}
                    style={headerLinkStyle}
                    onMouseEnter={(e) => { e.currentTarget.style.background = c.dayHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    {viewYear}
                  </button>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext}
              style={{
                width: "28px", height: "28px", borderRadius: "6px", border: "none",
                background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                color: c.text, cursor: canGoNext ? "pointer" : "not-allowed",
                opacity: canGoNext ? 1 : 0.4, display: "flex",
                alignItems: "center", justifyContent: "center", transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { if (canGoNext) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {pickerView !== "days" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", padding: "10px 8px", gap: "6px" }}>
              {(pickerView === "months"
                ? (isRTL ? MONTHS_AR : MONTHS_EN).map((name, m) => ({
                    key: m, label: name, onPick: () => selectMonth(m),
                    disabled: isMonthOutOfRange(viewYear, m),
                    selected: !!value && value.slice(0, 7) === `${viewYear}-${pad(m + 1)}`,
                    current: todayStr.slice(0, 7) === `${viewYear}-${pad(m + 1)}`,
                  }))
                : Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i).map((y) => ({
                    key: y, label: y, onPick: () => selectYear(y),
                    disabled: isYearOutOfRange(y),
                    selected: !!value && value.slice(0, 4) === String(y),
                    current: todayStr.slice(0, 4) === String(y),
                  }))
              ).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  disabled={item.disabled}
                  title={item.disabled ? outOfRangeTitle : undefined}
                  onClick={item.onPick}
                  onMouseEnter={(e) => { if (!item.disabled && !item.selected) e.currentTarget.style.background = c.dayHover; }}
                  onMouseLeave={(e) => { if (!item.disabled && !item.selected) e.currentTarget.style.background = item.current ? c.todayBg : "transparent"; }}
                  style={{
                    height: "38px", border: "none", borderRadius: "8px",
                    fontSize: "12px", fontWeight: item.selected ? "700" : item.current ? "600" : "500",
                    color: item.selected ? "#ffffff" : item.disabled ? c.textDim : item.current ? c.accent : c.text,
                    background: item.selected ? c.accent : item.current && !item.disabled ? c.todayBg : "transparent",
                    cursor: item.disabled ? "not-allowed" : "pointer",
                    opacity: item.disabled ? 0.45 : 1,
                    boxShadow: item.selected ? "0 2px 8px rgba(59, 130, 246, 0.35)" : "none",
                    transition: "all 0.15s ease", outline: "none", fontFamily: "inherit",
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {pickerView === "days" && (<>
          {/* ── Day names header ──────────────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "4px 8px 2px", gap: "2px" }}>
            {(isRTL ? DAYS_AR : DAYS_EN).map((day) => (
              <div
                key={day}
                style={{
                  textAlign: "center", fontSize: "10px", fontWeight: "700",
                  color: c.textMuted, padding: "4px 0", textTransform: "uppercase",
                  letterSpacing: "0.5px", userSelect: "none",
                }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* ── Calendar grid ──────────────────────────────────────────────── */}
          <div
            style={{
              display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
              padding: "4px 8px 8px", gap: "2px",
              opacity: animation ? 0.3 : 1,
              transform: animation === "slide-left" ? "translateX(-10px)" : animation === "slide-right" ? "translateX(10px)" : "translateX(0)",
              transition: "all 0.15s ease",
            }}
          >
            {calendarGrid.map((cell, idx) => {
              const isCurrentMonth = cell.type === "current";
              const dateStr = isCurrentMonth ? `${viewYear}-${pad(viewMonth + 1)}-${pad(cell.day)}` : null;
              const isSelected = dateStr === value;
              const isToday = dateStr === todayStr;
              const outOfRange = isOutOfRange(dateStr);
              const isSelectable = isCurrentMonth && !outOfRange;

              return (
                <button
                  key={idx}
                  type="button"
                  disabled={!isSelectable}
                  title={outOfRange ? outOfRangeTitle : undefined}
                  onClick={() => isSelectable && selectDay(cell.day)}
                  onMouseEnter={(e) => { if (isSelectable && !isSelected) e.currentTarget.style.background = c.dayHover; }}
                  onMouseLeave={(e) => { if (isSelectable && !isSelected) e.currentTarget.style.background = isToday ? c.todayBg : "transparent"; }}
                  style={{
                    width: "100%", aspectRatio: "1", maxHeight: "30px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "none", borderRadius: "6px", fontSize: "12px",
                    fontWeight: isSelected ? "700" : isToday ? "600" : "400",
                    color: isSelected ? "#ffffff" : !isCurrentMonth || outOfRange ? c.textDim : isToday ? c.accent : c.text,
                    background: isSelected ? c.accent : isToday && !outOfRange ? c.todayBg : "transparent",
                    cursor: isSelectable ? "pointer" : "not-allowed",
                    opacity: outOfRange && isCurrentMonth ? 0.45 : 1,
                    transition: "all 0.15s ease",
                    boxShadow: isSelected ? "0 2px 8px rgba(59, 130, 246, 0.35)" : "none",
                    outline: "none", fontFamily: "inherit",
                  }}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
          </>)}

          {/* ── Footer ────────────────────────────────────────────────────── */}
          <div style={{ display: "flex", justifyContent: "center", padding: "6px 8px 8px", borderTop: `1px solid ${c.calBorder}` }}>
            <button
              type="button"
              onClick={goToToday}
              disabled={isTodayOutOfRange}
              onMouseEnter={(e) => { if (!isTodayOutOfRange) e.currentTarget.style.background = c.accentHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = c.accent; }}
              style={{
                padding: "6px 20px", borderRadius: "8px", border: "none",
                background: c.accent, color: "#ffffff", fontSize: "12px",
                fontWeight: "600", cursor: isTodayOutOfRange ? "not-allowed" : "pointer",
                opacity: isTodayOutOfRange ? 0.5 : 1, transition: "all 0.2s",
                letterSpacing: "0.3px", fontFamily: "inherit",
              }}
            >
              {isRTL ? "اليوم" : "Today"}
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div style={{ position: "relative", ...outerStyle }}>
      {label && (
        <label
          style={{
            display: "flex", alignItems: "center", gap: "8px",
            marginBottom: "8px", fontSize: "14px", fontWeight: "600",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          {label}
        </label>
      )}

      {/* ── Trigger button ─────────────────────────────────────────────────── */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          width: "100%", padding: "12px 16px", borderRadius: "12px",
          border: `1px solid ${isFocused || isOpen ? c.borderFocus : c.border}`,
          background: isFocused || isOpen ? c.bgFocus : c.bg,
          color: c.text, direction: "ltr", fontSize: "14px", fontWeight: "500",
          outline: "none", transition: "all 0.3s ease", boxSizing: "border-box",
          cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: "12px",
          boxShadow: isFocused || isOpen
            ? (isDark ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.08)")
            : "none",
          textAlign: isRTL ? "right" : "left", fontFamily: "inherit",
        }}
      >
        <span style={{ flex: 1 }}>
          {value ? formatDisplayDate(value, isRTL) : placeholder || (isRTL ? "اختر التاريخ" : "Select Date")}
        </span>
        <Calendar
          size={18}
          color={c.iconColor}
          style={{
            transition: "transform 0.3s ease",
            transform: isOpen ? "scale(1.15)" : "scale(1)",
            opacity: isOpen ? 1 : 0.7,
          }}
        />
      </button>

      {calendarPortal}

      {/* ── Animation keyframes ────────────────────────────────────────────── */}
      <style>{`
        @keyframes dpFadeIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default DatePicker;
